import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Exercise, ExerciseEntry, GymId, Session, SetLog } from '../../domain/types';
import { suggestProgression } from '../../domain/progression';
import {
  getSession, putDraft, saveSession, deleteSession, exerciseMap, exerciseHistory, lastBodyweightKg,
  setLastGymId,
} from '../../db/repo';
import { GYMS, resolveExerciseForGym, resolveExerciseMapForGym } from '../../domain/gym';
import { entryForExercise, planWorkingSetsForGym, applyPlanToEntry } from './createSession';
import type { PlannedSet } from '../../domain/planner';
import { syncNow } from '../../sync/syncClient';
import { ExerciseCard, type EntryHints } from './ExerciseCard';
import { ExercisePicker } from './ExercisePicker';
import { RestTimer } from './RestTimer';
import { restSeconds } from './restDefaults';
import { Stepper, Toast, AutoTextarea } from '../../ui/primitives';
import { IconBack } from '../../ui/icons';
import { formatDate } from '../../lib/dates';

export function SessionScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [exById, setExById] = useState<Map<string, Exercise>>(new Map());
  const [hints, setHints] = useState<Map<string, EntryHints>>(new Map());
  const [picker, setPicker] = useState<null | { mode: 'add' } | { mode: 'sub'; entryIdx: number }>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [rest, setRest] = useState<{ endsAt: number; total: number } | null>(null);
  // Guards the gym picker while a re-plan is in flight, so a fast double-tap can't
  // land the session on one gym with the other gym's targets.
  const [gymBusy, setGymBusy] = useState(false);
  // Exercises are collapsed by default; this holds which are expanded. The jump-bar
  // and the card header both drive it.
  const [openIdxs, setOpenIdxs] = useState<Set<number>>(new Set());
  // Pre-fill value offered when Marko opts into logging bodyweight (it's optional).
  const [lastBw, setLastBw] = useState<number | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initial load (once).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, map, bw] = await Promise.all([getSession(id!), exerciseMap(), lastBodyweightKg()]);
      if (cancelled) return;
      if (!s) { setNotFound(true); return; }
      setSession(s);
      setExById(map);
      setLastBw(bw);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const toggleOpen = useCallback((idx: number) => {
    setOpenIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  // Open the target exercise and scroll its card into view (in-day navigation).
  const jumpTo = useCallback((idx: number) => {
    setOpenIdxs((prev) => new Set(prev).add(idx));
    requestAnimationFrame(() => cardRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);

  // The catalog as it behaves at THIS gym (increments differ between racks). Every
  // card and set row below reads from here, so nothing downstream needs to know.
  const gymExById = useMemo(
    () => resolveExerciseMapForGym(exById, session?.gymId ?? null),
    [exById, session?.gymId],
  );

  // Recompute engine hints whenever the set of exercises, or the gym, changes.
  // Changing the gym mid-session is a real correction (he realises he tapped the
  // wrong one), and it has to re-plan: different history, different targets.
  const exerciseKey = session?.entries.map((e) => e.exerciseId).join(',') ?? '';
  const sessionGymId = session?.gymId ?? null;
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      const next = new Map<string, EntryHints>();
      for (const entry of session.entries) {
        const ex = exById.get(entry.exerciseId);
        if (!ex) continue;
        const history = await exerciseHistory(ex.id, {
          excludeSessionId: session.id, limit: 6, gymId: sessionGymId,
        });
        const gymEx = resolveExerciseForGym(ex, sessionGymId);
        next.set(entry.exerciseId, { suggestion: suggestProgression(gymEx, history), beatThis: history[0]?.sets.filter((s) => !s.isWarmup) ?? [], prevNote: history[0]?.note ?? null });
      }
      if (!cancelled) setHints(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseKey, exById, sessionGymId]);

  // Debounced persistence. Drafts use putDraft (no sync); a re-opened finished
  // session re-saves (re-enqueues to the outbox).
  const persist = useCallback((s: Session) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (s.finishedAt) void saveSession(s);
      else void putDraft(s);
    }, 400);
  }, []);

  const mutate = useCallback((fn: (s: Session) => Session) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next = fn(prev);
      persist(next);
      return next;
    });
  }, [persist]);

  const updateEntry = useCallback((idx: number, fn: (e: ExerciseEntry) => ExerciseEntry) => {
    mutate((s) => ({ ...s, entries: s.entries.map((e, i) => (i === idx ? fn(e) : e)) }));
  }, [mutate]);

  if (notFound) {
    return <div className="screen"><div className="empty">Session not found.</div><button className="btn full" onClick={() => navigate('/')}>Home</button></div>;
  }
  if (!session) return <div className="screen"><div className="empty">Loading...</div></div>;

  const working = session.entries.flatMap((e) => e.sets.filter((s) => !s.isWarmup));
  const loggedCount = working.filter((s) => s.logged !== false).length;

  function patchSet(entryIdx: number, setIdx: number, patch: Partial<SetLog>) {
    updateEntry(entryIdx, (e) => ({ ...e, sets: e.sets.map((s, i) => (i === setIdx ? { ...s, ...patch } : s)) }));
  }
  function toggleLogged(entryIdx: number, setIdx: number) {
    const entry = session!.entries[entryIdx];
    const set = entry?.sets[setIdx];
    const turningOn = set?.logged === false;
    updateEntry(entryIdx, (e) => ({
      ...e,
      sets: e.sets.map((s, i) => (i === setIdx ? { ...s, logged: s.logged === false ? true : false } : s)),
    }));
    // Auto-start the rest timer when a real (non-warmup) set is logged.
    if (turningOn && set && !set.isWarmup) {
      const ex = exById.get(entry.exerciseId);
      if (ex) {
        const total = restSeconds(ex);
        setRest({ endsAt: Date.now() + total * 1000, total });
      }
      // Auto-collapse the card once its last working set is logged, so the next
      // exercise is front-and-centre. Compute against the just-flipped set.
      const working = entry.sets.filter((s) => !s.isWarmup);
      const allNowLogged = working.length > 0 && working.every((s) => (s === set ? true : s.logged !== false));
      if (allNowLogged) {
        setOpenIdxs((prev) => {
          if (!prev.has(entryIdx)) return prev;
          const next = new Set(prev);
          next.delete(entryIdx);
          return next;
        });
      }
    }
  }
  function addSet(entryIdx: number) {
    updateEntry(entryIdx, (e) => {
      const last = e.sets[e.sets.length - 1];
      const clone: SetLog = last
        ? { ...last, setNumber: e.sets.length + 1, isWarmup: false, logged: false }
        : { setNumber: 1, isWarmup: false, weightKg: 0, reps: 8, rir: 1, isFailure: false, logged: false };
      return { ...e, sets: [...e.sets, clone] };
    });
  }
  function removeSet(entryIdx: number, setIdx: number) {
    updateEntry(entryIdx, (e) => ({ ...e, sets: e.sets.filter((_, i) => i !== setIdx) }));
  }
  function removeEntry(entryIdx: number) {
    mutate((s) => ({ ...s, entries: s.entries.filter((_, i) => i !== entryIdx).map((e, i) => ({ ...e, order: i })) }));
  }
  // Reorder exercises so the logged order reflects what was actually done first
  // (e.g. Nordics run first vs third). order is reindexed and synced to Notion.
  function moveEntry(entryIdx: number, dir: -1 | 1) {
    const j = entryIdx + dir;
    mutate((s) => {
      if (j < 0 || j >= s.entries.length) return s;
      const entries = [...s.entries];
      [entries[entryIdx], entries[j]] = [entries[j], entries[entryIdx]];
      return { ...s, entries: entries.map((e, i) => ({ ...e, order: i })) };
    });
    setOpenIdxs(new Set());
  }

  // Correcting the gym has to re-plan, not just re-label: the pre-filled loads were
  // computed from the other gym's history and are the numbers he'd otherwise lift.
  // Already-logged sets are real observations and survive untouched.
  async function changeGym(next: GymId) {
    const current = session!;
    if (current.gymId === next || gymBusy) return;
    setGymBusy(true);
    try {
      await setLastGymId(next);
      // Compute the new plans first (async, reads history per exercise)...
      const plans = new Map<string, PlannedSet[]>();
      await Promise.all(
        current.entries.map(async (entry) => {
          const ex = exById.get(entry.exerciseId);
          if (!ex) return;
          const workingCount = entry.sets.filter((s) => !s.isWarmup).length;
          plans.set(entry.exerciseId, await planWorkingSetsForGym(ex, workingCount, next));
        }),
      );
      // ...then apply them inside the updater, against whatever the session is NOW.
      // He may have logged a set while this was running; those carry logged !== false
      // and applyPlanToEntry leaves them untouched.
      mutate((s) => ({
        ...s,
        gymId: next,
        entries: s.entries.map((e) => {
          const planned = plans.get(e.exerciseId);
          return planned ? applyPlanToEntry(e, planned) : e;
        }),
      }));
      setToast(`Switched to ${GYMS.find((g) => g.id === next)?.shortName}. Unlogged targets re-planned.`);
    } finally {
      setGymBusy(false);
    }
  }

  async function addExercise(ex: Exercise) {
    const newIdx = session!.entries.length;
    const entry = await entryForExercise(ex, newIdx, 3, session!.gymId);
    mutate((s) => ({ ...s, entries: [...s.entries, entry] }));
    setOpenIdxs((prev) => new Set(prev).add(newIdx)); // a freshly added exercise opens
    setPicker(null);
  }
  async function substitute(entryIdx: number, ex: Exercise) {
    const original = session!.entries[entryIdx];
    const built = await entryForExercise(
      ex, original.order, original.sets.filter((s) => !s.isWarmup).length || 3, session!.gymId,
    );
    mutate((s) => ({
      ...s,
      entries: s.entries.map((e, i) => (i === entryIdx
        ? { ...built, substitutedFrom: original.substitutedFrom ?? original.exerciseId }
        : e)),
    }));
    setPicker(null);
  }

  async function finish() {
    // Keep only performed (logged) sets; drop exercises with nothing logged.
    const cleaned: ExerciseEntry[] = session!.entries
      .map((e) => ({ ...e, sets: e.sets.filter((s) => s.logged !== false).map(({ logged: _logged, ...rest }) => rest) }))
      .filter((e) => e.sets.length > 0)
      .map((e, i) => ({ ...e, order: i }));
    if (cleaned.length === 0) {
      setToast('Log at least one set first.');
      return;
    }
    // A 0 kg working set on a loaded exercise is always a mis-log — most likely a
    // first-ever session at a gym, where there's no history to pre-fill from and the
    // stepper started at 0. Left in, it becomes that gym's baseline and the engine
    // happily "progresses" from zero. Bodyweight exercises legitimately carry 0
    // (no added load), so they're exempt.
    const zeroLoad = cleaned.find((e) => {
      const ex = exById.get(e.exerciseId);
      return ex && !ex.bodyweight && e.sets.some((s) => !s.isWarmup && s.weightKg === 0);
    });
    if (zeroLoad) {
      const name = exById.get(zeroLoad.exerciseId)?.name ?? 'An exercise';
      setToast(`${name} has a set at 0 kg. Enter the weight you used before finishing.`);
      return;
    }
    const finished: Session = { ...session!, entries: cleaned, finishedAt: new Date().toISOString() };
    await saveSession(finished);
    // Push immediately when online; saveSession already queued it to the outbox, so
    // this is a safe no-op offline / when sync isn't configured. Don't block nav on it.
    void syncNow();
    navigate('/', { replace: true });
  }

  async function discard() {
    if (!confirm('Discard this session?')) return;
    await deleteSession(session!.id);
    navigate('/', { replace: true });
  }

  return (
    <div className="screen">
      <div className="spread" style={{ marginBottom: 6 }}>
        <button className="btn ghost sm" onClick={() => navigate('/')}><IconBack width={18} height={18} /> Back</button>
        <span className="chip accent">{loggedCount}/{working.length} sets</span>
      </div>

      <div className="card">
        <input
          className="input"
          value={session.routineLabel}
          onChange={(e) => mutate((s) => ({ ...s, routineLabel: e.target.value }))}
          style={{ fontWeight: 700, marginBottom: 8 }}
        />
        <div className="row wrap" style={{ gap: 12 }}>
          <div>
            <label className="faint" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Date</label>
            <input className="input" type="date" value={session.date} onChange={(e) => mutate((s) => ({ ...s, date: e.target.value }))} style={{ minHeight: 40 }} />
          </div>
          <div>
            <label className="faint" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Bodyweight <span className="faint">(optional)</span>
            </label>
            {session.bodyweightKg == null ? (
              <button
                className="btn ghost"
                style={{ minHeight: 48 }}
                onClick={() => mutate((s) => ({ ...s, bodyweightKg: lastBw ?? 80 }))}
              >
                Add bodyweight
              </button>
            ) : (
              <div className="row" style={{ gap: 8 }}>
                <Stepper
                  value={session.bodyweightKg}
                  step={0.5}
                  min={40}
                  unit="kg"
                  onChange={(v) => mutate((s) => ({ ...s, bodyweightKg: v }))}
                />
                <button
                  className="btn ghost sm"
                  onClick={() => mutate((s) => ({ ...s, bodyweightKg: null }))}
                  aria-label="clear bodyweight"
                >
                  Skip
                </button>
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <label className="faint" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Gym</label>
          <div className="segmented full" aria-label="Gym">
            {GYMS.map((g) => (
              <button
                key={g.id}
                type="button"
                className={g.id === session.gymId ? 'on' : ''}
                disabled={gymBusy}
                onClick={() => void changeGym(g.id)}
              >
                {g.shortName}
              </button>
            ))}
          </div>
        </div>
        <AutoTextarea
          placeholder="Session note (energy, soreness, stress)"
          value={session.sessionNote}
          onChange={(sessionNote) => mutate((s) => ({ ...s, sessionNote }))}
          style={{ marginTop: 10 }}
        />
        <div className="faint" style={{ fontSize: 12, marginTop: 8 }}>
          {session.finishedAt ? 'Finished session (edits re-sync)' : 'Draft - autosaved offline'} - {formatDate(session.date)}
        </div>
      </div>

      {session.entries.length > 1 && (
        <div className="jumpbar">
          {session.entries.map((entry, idx) => {
            const ex = exById.get(entry.exerciseId);
            if (!ex) return null;
            const ws = entry.sets.filter((s) => !s.isWarmup);
            const done = ws.length > 0 && ws.every((s) => s.logged !== false);
            return (
              <button
                key={`${entry.exerciseId}-${idx}`}
                className={`chip ${done ? 'good' : ''}`}
                onClick={() => jumpTo(idx)}
              >
                {idx + 1}. {ex.name}
              </button>
            );
          })}
        </div>
      )}

      {session.entries.map((entry, idx) => {
        const ex = gymExById.get(entry.exerciseId);
        if (!ex) return null;
        return (
          <div key={`${entry.exerciseId}-${idx}`} ref={(el) => { cardRefs.current[idx] = el; }} style={{ scrollMarginTop: 64 }}>
            <ExerciseCard
              ex={ex}
              entry={entry}
              hints={hints.get(entry.exerciseId)}
              open={openIdxs.has(idx)}
              onToggleOpen={() => toggleOpen(idx)}
              onPatchEntry={(patch) => updateEntry(idx, (e) => ({ ...e, ...patch }))}
              onPatchSet={(setIdx, patch) => patchSet(idx, setIdx, patch)}
              onToggleLogged={(setIdx) => toggleLogged(idx, setIdx)}
              onAddSet={() => addSet(idx)}
              onRemoveSet={(setIdx) => removeSet(idx, setIdx)}
              onSubstitute={() => setPicker({ mode: 'sub', entryIdx: idx })}
              onRemoveEntry={() => removeEntry(idx)}
              onMoveUp={() => moveEntry(idx, -1)}
              onMoveDown={() => moveEntry(idx, 1)}
              canMoveUp={idx > 0}
              canMoveDown={idx < session.entries.length - 1}
            />
          </div>
        );
      })}

      <button className="btn ghost full" onClick={() => setPicker({ mode: 'add' })} style={{ marginTop: 4 }}>
        Add exercise
      </button>

      <div className="row" style={{ gap: 10, marginTop: 16 }}>
        <button className="btn danger" onClick={discard}>Discard</button>
        <button className="btn primary grow" onClick={finish}>Finish session</button>
      </div>

      {picker?.mode === 'add' && (
        <ExercisePicker title="Add exercise" onPick={addExercise} onClose={() => setPicker(null)} />
      )}
      {picker?.mode === 'sub' && (
        <ExercisePicker
          title="Substitute exercise"
          excludeId={session.entries[picker.entryIdx]?.exerciseId}
          onPick={(ex) => substitute(picker.entryIdx, ex)}
          onClose={() => setPicker(null)}
        />
      )}
      {rest && (
        <RestTimer
          endsAt={rest.endsAt}
          total={rest.total}
          onAdjust={(delta) => setRest((r) => (r ? { ...r, endsAt: r.endsAt + delta * 1000, total: Math.max(r.total, (r.endsAt + delta * 1000 - Date.now()) / 1000) } : r))}
          onDismiss={() => setRest(null)}
        />
      )}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
